<template>
  <div
    v-loading.absolute="askBidLoading"
    v-resize="getContainerHeight"
    class="dom-table"
    :class="{ 'table-lock': !domScrollable }"
  >
    <DomTableHeader />
    <VirtualList
      v-if="displayList.length"
      ref="virtulaList"
      :list="displayList"
      :item-height="itemHeight"
      class="list-content"
      @scroll="debounceEvent"
    >
      <template v-if="!askBidLoading" slot-scope="props">
        <DomTableItem
          v-for="(item, idx) in props.visibleData"
          :key="idx"
          :data="item"
          :order-map="orderMap"
          :price-map="priceMap"
          :is-tradable="isTradable"
          :is-active="item.price === priceMapLatestPrice"
          :cost="avgCost"
          @cancelOrder="cancelOrder"
          @tradeStock="tradeStock"
        />
      </template>
    </VirtualList>
  </div>
</template>
<script>
import VirtualList from 'com/VirtualList'
import DomTableItem from './DomTableItem'
import domAskbidDataMixin from '../mixins/domAskbidDataMixin'
import domOrderDataMixin from '../mixins/domOrderDataMixin'
import stockMixin from 'mixins/stockMixin'
import DomTableHeader from './DomTableHeader'
import { mapGetters } from 'vuex'
import debounce from 'lodash/debounce'
import * as util from './util'
import getPriceMinTick from 'utils/getPriceMinTick'
import { TRADING_STATUS } from 'const/stock'
import getFixedPriceWithMinTick from 'utils/getFixedPriceWithMinTick'

export default {
  name: 'DomTable',
  components: {
    DomTableItem,
    DomTableHeader,
    VirtualList
  },
  mixins: [domAskbidDataMixin, domOrderDataMixin, stockMixin],
  props: {
    stock: Object,
    minTicks: Array,
    holding: Object,
    isTradable: Boolean,
    symbol: String
  },
  computed: {
    ...mapGetters([
      'curStockOrders',
      'hasUsLv2Permission',
      'hasHkLv0Permission',
      'hasUsLv0Permission',
      'domScrollable',
      'isVirtual'
    ]),
    avgCost() {
      if (this.holding && this.holding.averageCost) {
        return util.getNearbyPrice(+this.holding.averageCost, this.minTicks)
      }
      return -1
    },
    initCount() {
      return Math.ceil((this.tableWrapHeight - 35) / this.itemHeight)
    },
    latestPrice() {
      return this.stock.tradingStatus === TRADING_STATUS.TRADING
        ? this.stock.latestPrice || 0
        : this.stock.hourTradingLatestPrice || this.stock.latestPrice || 0
    },
    minTick() {
      return getPriceMinTick(this.latestPrice, this.minTicks) || 0.01
    },
    priceMapLatestPrice() {
      return getFixedPriceWithMinTick(this.latestPrice, this.minTick, true)
    }
  },
  watch: {
    symbol(newSymbol, oldSymbol) {
      this.getAskBidBySymbol(true, true)
      this.unsubAskBid(oldSymbol)
      if (this.isUS) {
        this.subAskBid(newSymbol)
      }
    },
    priceMapLatestPrice(latestPrice) {
      if (latestPrice) {
        this.getAskBidBySymbol(false)
      }
    },
    domScrollable() {
      this.buildTableData()
    },
    minTicks() {
      this.buildTableData()
    },
    initCount() {
      this.buildTableData()
    }
  },
  data() {
    return {
      itemHeight: 15,
      displayList: [],
      tableWrapHeight: 0,
      debounceEvent: null,
      lastLatestPrice: 0,
      lastMaxPrice: 0 // 价格列更新前顶部最大价格
    }
  },
  created() {
    this.subAskBid(this.symbol)
    this.subscribeQuotes()
  },
  mounted() {
    this.getAskBidBySymbol(true, true)
    this.debounceEvent = debounce(this.scrollHandler, 100)
  },
  beforeDestroy() {
    this.unsubAskBid(this.symbol)
    this.unsubscribeQuotes()
  },
  methods: {
    cancelOrder(data) {
      this.$emit('cancelOrder', data)
    },
    tradeStock(data) {
      this.$emit('tradeStock', data)
    },
    getContainerHeight() {
      this.$nextTick(() => {
        this.tableWrapHeight = this.$el.clientHeight
      })
    },
    buildTableData(needResetPos = true, lastTop = 0) {
      this.priceMap.latestPrice = this.priceMapLatestPrice
      const latestPrice = this.priceMap.latestPrice
      const latestItem = this.priceMap[latestPrice] || {}
      if (needResetPos || !this.domScrollable) {
        const res = [
          {
            price: latestPrice,
            type: latestItem.type,
            volume: latestItem.volume
          }
        ]
        const addCount = Math.ceil(this.initCount / 2)
        this.addBefore(this.domScrollable ? 500 : addCount * 2, res)
        this.addAfter(this.domScrollable ? 500 : addCount, res)
        this.displayList = res

        if (lastTop) {
          const offsetHeight =
            util.getOffsetCount(
              latestPrice,
              this.lastLatestPrice,
              this.minTicks,
              this.isUS
            ) * this.itemHeight
          lastTop += offsetHeight
        }

        this.$nextTick(() => {
          const listRef = this.$refs.virtulaList
          if (lastTop) {
            // 根据边界变化重置scrollTop
            listRef && listRef.scrollListTop(lastTop)
          } else {
            if (this.domScrollable) {
              listRef &&
                listRef.scrollListTop((501 - addCount) * this.itemHeight)
            } else {
              listRef && listRef.scrollListTop((addCount + 1) * this.itemHeight)
            }
          }
          this.lastLatestPrice = latestPrice
          this.lastMaxPrice = res[0].priceMap
        })
      } else {
        // dispalyList价格区间不变，只更新数量
        this.displayList = this.displayList.map(item => {
          const hash = this.priceMap[item.price]
          return {
            price: item.price,
            volume: hash ? hash.volume : item.volume,
            type: hash ? hash.type : item.type
          }
        })
      }
    },
    addBefore(count, res) {
      let idx = 1
      let lastValue = this.priceMap.latestPrice
      while (idx <= count) {
        const bigVal = util.getNextPrice(
          lastValue,
          this.minTicks,
          this.isUS,
          true
        )
        lastValue = bigVal
        const bigItem = this.priceMap[lastValue] || {}
        res.unshift({
          price: bigVal,
          volume: bigItem.volume,
          type: bigItem.type || 'ask'
        })
        idx++
      }
    },
    addAfter(count, res) {
      let idx = 1
      let lastValue = this.priceMap.latestPrice
      while (idx <= count) {
        const smallVal = util.getNextPrice(
          lastValue,
          this.minTicks,
          this.isUS,
          false
        )
        lastValue = smallVal
        if (Number(smallVal) > 0) {
          const smallItem = this.priceMap[lastValue] || {}
          res.push({
            price: smallVal,
            volume: smallItem.volume,
            type: smallItem.type || 'bid'
          })
        }
        idx++
      }
    },
    scrollHandler(top) {
      // 可滚动状态下，如果滚动后现价变化了，需要更新价格边界
      if (
        this.domScrollable &&
        this.lastLatestPrice !== this.priceMap.latestPrice &&
        !this.askBidLoading
      ) {
        this.buildTableData(true, top)
      }
    }
  }
}
</script>
<style lang="less">
.dom-table {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  padding: 0 10px 10px;
  border-bottom: 1px solid var(--special-color);
  .list-content {
    flex: 1;
    min-height: 0;
  }
  &.table-lock {
    overflow: hidden;
    .list-content {
      overflow: hidden;
    }
  }
  .dom-table-item,
  .dom-table-header {
    height: 15px;
    margin: 0;
    padding: 0;
    font-size: 12px;
    display: flex;
    flex-direction: row;
    align-content: center;
    justify-content: space-between;
    span {
      display: inline-block;
      text-align: center;
      &:nth-of-type(1),
      &:nth-of-type(5) {
        width: 50px;
        flex-shrink: 0;
      }
      &:nth-of-type(2),
      &:nth-of-type(4) {
        width: 55px;
        flex-shrink: 0;
      }
      &:nth-of-type(3) {
        display: flex;
        flex-direction: row;
        justify-content: center;
        align-items: center;
        flex-shrink: 0;
        width: 90px;
      }
    }
  }
}
</style>
